import { useEffect } from 'react';
import { useOnlinePlayers, usePresence } from './hooks/useOnlinePlayers';
import { useAuthStore } from './stores/useAuthStore';
import Navbar from './components/Navbar';
import { Users, Gamepad2, MessageSquare, Globe } from 'lucide-react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import PlayNow from './components/PlayNow';
import UserProfile from './components/UserProfile';
import GameHistory from './components/GameHistory';
import FriendRequests from './components/FriendRequests';
import Leaderboard from './components/Leaderboard';
import PublicProfile from './components/PublicProfile';
import InviteNotification from './components/InviteNotification';
import SpectatorList from './components/SpectatorList';
import SpectatorView from './components/SpectatorView';
import DiscordCallback from './components/DiscordCallback';
import PrivacyPolicy from './components/PrivacyPolicy';
import AdminPanel from './components/AdminPanel';
import Footer from './components/Footer';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';

function HomePage() {
  const navigate = useNavigate();
  const { onlinePlayers, isConnected } = useOnlinePlayers();

  const features = [
    {
      icon: <Gamepad2 className="w-8 h-8 text-pink-400" />,
      title: "Real-time Multiplayer",
      description: "Challenge players worldwide in classic games like Battleships, Tic Tac Toe, and Checkers"
    },
    {
      icon: <Users className="w-8 h-8 text-violet-400" />,
      title: "No Account Needed",
      description: "Jump straight into games - no sign up required. Just pick a name and play!"
    },
    {
      icon: <MessageSquare className="w-8 h-8 text-blue-400" />,
      title: "Video, Voice & Text Chat",
      description: "Connect with opponents using your webcam, microphone, or text messages"
    },
    {
      icon: <Globe className="w-8 h-8 text-green-400" />,
      title: "Global Community",
      description: "Join players from around the world in friendly competition"
    }
  ];

  return (
    <div className="py-12 sm:py-20">
      <div className="text-center mb-16">
        <h1 className="text-4xl sm:text-6xl font-bold mb-6 bg-gradient-to-r from-pink-500 via-violet-500 to-blue-500 text-transparent bg-clip-text">
          Play Games. Meet People.
        </h1>
        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
          Challenge players worldwide in real-time multiplayer games. No account needed - just pick a name and start playing!
        </p>
        {isConnected && onlinePlayers > 0 && (
          <p className="text-green-400 text-lg animate-pulse">
            {onlinePlayers} Players Online Now
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
        {features.map((feature, index) => (
          <div
            key={index}
            className="bg-white/5 backdrop-blur-lg rounded-xl p-6 hover:bg-white/10 transition"
          >
            <div className="mb-4">{feature.icon}</div>
            <h2 className="text-xl font-semibold text-white mb-2">{feature.title}</h2>
            <p className="text-gray-300">{feature.description}</p>
          </div>
        ))}
      </div>

      <div className="text-center">
        <button
          onClick={() => navigate('/play')}
          className="px-8 py-4 bg-gradient-to-r from-pink-600 to-violet-600 text-white rounded-lg text-lg font-semibold hover:opacity-90 transition"
        >
          Start Playing Now
        </button>
      </div>
    </div>
  );
}

const App: React.FC = () => {
  const initializeAuth = useAuthStore(state => state.initializeAuth);
  const authLoading = useAuthStore(state => state.loading);
  useEffect(() => { initializeAuth(); }, [initializeAuth]);
  usePresence();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-900 via-slate-900 to-black flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-pink-500 animate-spin" />
      </div>
    );
  }

  return (
    <Router>
      <Toaster theme="dark" position="top-center" richColors />
      <div className="min-h-screen bg-gradient-to-br from-violet-900 via-slate-900 to-black">
        <Navbar />
        <InviteNotification />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/play" element={<PlayNow />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/history" element={<GameHistory />} />
            <Route path="/friends" element={<FriendRequests />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/player/:username" element={<PublicProfile />} />
            <Route path="/watch" element={<SpectatorList />} />
            <Route path="/watch/:gameId" element={<SpectatorView />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/auth/discord/callback" element={<DiscordCallback />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
};

export default App;
